import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, X, LayoutDashboard } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { DynamicChart } from './DynamicChart';

interface Props {
  sourceId: string;
  sourceName: string;
  onClear: () => void;
}

/** Shows only the charts that came from one specific source (e.g. a workflow run). */
export const SourceScopedDashboard = ({ sourceId, sourceName, onClear }: Props) => {
  const { data: charts, isLoading } = useQuery({
    queryKey: ['scoped-charts', sourceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('saved_charts')
        .select('*')
        .eq('source_id', sourceId)
        .order('position');
      if (error) throw error;
      return data || [];
    },
  });

  return (
    <Card className="p-6 space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <LayoutDashboard className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">Dashboard for {sourceName}</h2>
        <Badge variant="outline">Scoped view</Badge>
        <Button variant="ghost" size="sm" className="ml-auto" onClick={onClear}>
          <X className="h-4 w-4 mr-1" /> Show everything
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading this dashboard…
        </div>
      ) : !charts?.length ? (
        <p className="text-sm text-muted-foreground">
          No charts were saved for {sourceName} yet. Run it again to build them.
        </p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {charts.map((c: any) => (
            <DynamicChart
              key={c.id}
              config={{
                id: c.id,
                type: (c.chart_type || 'bar') as 'bar' | 'line' | 'pie' | 'area',
                title: c.title,
                data: (c.data as any[]) || [],
                xAxis: c.x_field || 'name',
                yAxis: c.y_field || 'value',
              }}
              data={(c.data as any[]) || []}
            />
          ))}
        </div>
      )}
    </Card>
  );
};
